import {
  PauseErrorCode,
  PauseState,
  SubscriptionCardStatus,
  SubscriptionPause,
} from '../types/subscription-pause.types';

/**
 * Membership pause rules (CWS-1469) that the UI needs before or without a round trip.
 * Pure: no Angular, no HTTP, no display text (see subscription-pause.copy.ts).
 *
 * Dates are local calendar days. The API reads a bare `YYYY-MM-DD` as midnight at the site, so
 * the browser's calendar day is what gets sent; the server still validates against site time.
 */

/** PRD rules section: a pause month is 30 days, so 4 months = 120 days = MAX_PAUSE_DAYS. */
export const PAUSE_MONTH_DAYS = 30;
export const MIN_PAUSE_MONTHS = 1;
export const MAX_PAUSE_MONTHS = 4;
/** PRD P-11: a pause runs 7 to 120 days (resume date minus start date). */
export const MIN_PAUSE_DAYS = 7;
export const MAX_PAUSE_DAYS = 4 * PAUSE_MONTH_DAYS;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface PauseWindow {
  start: Date;
  /** First day the member can wash again; sent as `resume_date`. */
  resumeDate: Date;
  /** Last day of the pause: the day before `resumeDate`. */
  lastPausedDay: Date;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Calendar-day arithmetic, so a daylight saving change never shifts the day. */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function isSameDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

/** Whole calendar days from `from` to `to`. */
export function daysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);
}

/** Local `YYYY-MM-DD`. Never `toISOString()`, which converts to UTC and can change the day. */
export function toYmd(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Parses an API date (ISO instant or `YYYY-MM-DD`) into a Date; null when missing or invalid. */
/** A payload's pause block, or null when absent (feature off) or not shaped like one. */
export function readSubscriptionPause(raw: unknown): SubscriptionPause | null {
  const row =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  const state = String(row?.['state'] ?? '');
  return row && ['none', 'scheduled', 'paused', 'resumed'].includes(state)
    ? (row as unknown as SubscriptionPause)
    : null;
}

export function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function clampPauseMonths(months: number): number {
  return Math.min(MAX_PAUSE_MONTHS, Math.max(MIN_PAUSE_MONTHS, Math.trunc(months)));
}

export function pauseWindow(start: Date, months: number): PauseWindow {
  const first = startOfDay(start);
  const resumeDate = addDays(first, clampPauseMonths(months) * PAUSE_MONTH_DAYS);
  return { start: first, resumeDate, lastPausedDay: addDays(resumeDate, -1) };
}

/** The window for an explicit last paused day; `resumeDate` is the day after it. */
export function pauseWindowThrough(start: Date, lastPausedDay: Date): PauseWindow {
  const first = startOfDay(start);
  const last = startOfDay(lastPausedDay);
  return { start: first, resumeDate: addDays(last, 1), lastPausedDay: last };
}

/** Pause length in days: resume date minus start (PRD P-11). */
export function pauseLengthDays(window: PauseWindow): number {
  return daysBetween(window.start, window.resumeDate);
}

export function pauseLengthError(days: number): 'TOO_SHORT' | 'TOO_LONG' | null {
  if (days < MIN_PAUSE_DAYS) {
    return 'TOO_SHORT';
  }
  return days > MAX_PAUSE_DAYS ? 'TOO_LONG' : null;
}

/** Next quick-pick length (a whole number of 30-day months) above or below `days`. */
export function stepPauseDays(days: number, direction: 1 | -1): number {
  const months = days / PAUSE_MONTH_DAYS;
  const next = direction === 1 ? Math.floor(months) + 1 : Math.ceil(months) - 1;
  return clampPauseMonths(next) * PAUSE_MONTH_DAYS;
}

/**
 * Refusals that mean the membership changed since the card loaded (another terminal, the lane,
 * the B2C app). The host reloads its cards and tells the user to refresh.
 */
export const STALE_PAUSE_CODES: ReadonlySet<PauseErrorCode> = new Set<PauseErrorCode>([
  'ALREADY_PAUSED',
  'NOT_PAUSED',
  'NOT_SCHEDULED',
  'NOT_ACTIVE',
  'CANCEL_PENDING',
  'BILLING_RESTART_PENDING',
  'PLAN_CHANGE_PENDING',
  'SUBSCRIPTION_NOT_FOUND',
]);

/**
 * Refusals after which the host reloads its cards. The stale codes, plus the ones that mean this
 * membership can never be paused: the card offered Pause from data that is now out of date, and
 * the reload hides it (CWS-1737). Their copy stays specific; see pauseRefusalCopy.
 */
export const RELOAD_PAUSE_CODES: ReadonlySet<PauseErrorCode> = new Set<PauseErrorCode>([
  ...STALE_PAUSE_CODES,
  'PAUSE_DISABLED',
  'FAMILY_PLAN',
  'NOT_ELIGIBLE',
]);

/**
 * The calendar day of an instant at the site, as that day's local midnight in the browser. The
 * server reads every date at the site's zone, so day maths on this value agrees with it wherever
 * the browser is. Without a usable zone it is the browser's own day.
 */
export function zonedDay(value: Date, timeZone?: string | null): Date {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(value);
      const part = (type: string) => Number(parts.find(p => p.type === type)?.value);
      const day = new Date(part('year'), part('month') - 1, part('day'));
      if (!Number.isNaN(day.getTime())) {
        return day;
      }
    } catch {
      // An unknown zone name: fall through to the browser's day.
    }
  }
  return startOfDay(value);
}

/** Today at the site. */
export function todayIn(timeZone?: string | null): Date {
  return zonedDay(new Date(), timeZone);
}

/**
 * The renewal date once the pause ends, matching `computePause` on the server: the days left in
 * the paid period at the pause start are banked and added to the resume date. `periodEnd` is the
 * period end the server banks from, read as a day at the site. A start after it rolls the
 * renewal forward by months first, as the start job will.
 */
export function projectedNextRenewal(
  periodEnd: string | Date | null,
  start: Date,
  resumeDate: Date,
  timeZone?: string | null
): Date | null {
  const instant = toDate(periodEnd);
  if (!instant) {
    return null;
  }
  const base = zonedDay(instant, timeZone);
  const startDay = startOfDay(start);
  let renewal = base;
  let months = 0;
  while (renewal < startDay) {
    months += 1;
    renewal = addMonthsClamped(base, months);
  }
  return addDays(resumeDate, daysBetween(startDay, renewal));
}

/** Same day `months` later, clamped to the month's last day (Jan 31 + 1 → Feb 28), as on the server. */
export function addMonthsClamped(date: Date, months: number): Date {
  const daysInTarget = new Date(date.getFullYear(), date.getMonth() + months + 1, 0).getDate();
  return new Date(
    date.getFullYear(),
    date.getMonth() + months,
    Math.min(date.getDate(), daysInTarget)
  );
}

/** The pause-related date a card shows in place of one of its stats. */
export interface PauseStat {
  key: 'resumes_on' | 'pause_starts';
  date: Date;
}

export interface PauseManageState {
  /** False hides the Manage Subscription menu: nothing in it can be used. */
  visible: boolean;
  /**
   * Update is offered. Hidden, never shown disabled, whenever it cannot be used: plan changes
   * switched off at the location or an imported membership (CWS-1734), a paused or scheduled
   * pause (CWS-1470), or billing still held after a resume (CWS-1661).
   */
  updateVisible: boolean;
  /**
   * Pause is offered. Like Update, it is shown only when it can be used and never disabled
   * (CWS-1737): any refusal, or a role that cannot manage memberships, hides it.
   */
  pauseVisible: boolean;
  /** Replace Pause with "Cancel scheduled pause". */
  showCancelScheduled: boolean;
  /**
   * Cancel membership is offered (CWS-1780). The host answers from the row's `can_cancel` and the
   * location's cancel & refund availability; a pause does not hide it, the server settles any
   * interlock (a paused membership cancels with no prorated refund).
   */
  cancelVisible: boolean;
  /**
   * CWS-1536: Payment method (change the renewal card) is offered. Off unless the host passes
   * `paymentMethodAvailable`: the Subscriptions tab and both lane pop-ups do.
   */
  paymentMethodVisible: boolean;
  /**
   * CWS-1581: Start Family is offered. The host answers (live Way membership on a family-enabled
   * package, Family Plan on at the location); any role may start one, like a family sale.
   */
  startFamilyVisible: boolean;
}

export interface PauseViewState {
  state: PauseState;
  stat: PauseStat | null;
  banner: 'paused' | 'scheduled' | null;
  showActivate: boolean;
  manage: PauseManageState;
}

/**
 * The tag a card shows. The API reports a paused or scheduled membership as `display_status:
 * 'active'` and puts the pause in `pause.state`, so those two tags are derived here. A pending
 * cancellation keeps its own tag: it outranks a pause.
 */
export function cardStatus(source: {
  display_status: string;
  is_live: boolean;
  pause: SubscriptionPause | null;
}): SubscriptionCardStatus {
  if (source.is_live && source.display_status === 'active') {
    if (source.pause?.state === 'paused') {
      return 'paused';
    }
    if (source.pause?.state === 'scheduled') {
      return 'scheduled_pause';
    }
  }
  return source.display_status as SubscriptionCardStatus;
}

/** The fields of a membership the pause view depends on; satisfied by list cards and lane payloads. */
export interface PauseViewSource {
  is_live: boolean;
  pause: SubscriptionPause | null;
}

/**
 * How a membership card renders around a pause. Shared by the Customers tab and the lane popups
 * so both apply the same rules. `updateAvailable` is the host's own answer for Update (plan
 * change eligibility and tenant settings). `canManage` is whether the signed-in role may pause or
 * reactivate (Attendant+ and Admin): for anyone else the pause actions are hidden, not disabled,
 * and only Update remains.
 */
export function pauseViewState(
  source: PauseViewSource,
  {
    updateAvailable,
    canManage,
    cancelAvailable = false,
    paymentMethodAvailable = false,
    startFamilyAvailable = false,
  }: {
    updateAvailable: boolean;
    canManage: boolean;
    cancelAvailable?: boolean;
    paymentMethodAvailable?: boolean;
    startFamilyAvailable?: boolean;
  }
): PauseViewState {
  const pause = source.pause;
  const state: PauseState = pause?.state ?? 'none';
  const showCancelScheduled = canManage && pause?.can_cancel_scheduled === true;
  // CWS-1737: Pause is offered only when it can be used. No pause block means pausing is switched
  // off; any refusal (family plan, cancelling, overdue, ...) hides it rather than disabling it.
  const pauseVisible = canManage && !showCancelScheduled && pause?.can_pause === true;
  // CWS-1661: billing is still held after a resume, and svc-order refuses a plan change until it
  // restarts (`pause_in_progress`), so Update is not offered meanwhile. Same as `can_change_plan`.
  // CWS-1734: an Update that cannot be used is hidden, not shown disabled with a tooltip.
  const updateVisible = updateAvailable && state === 'none';
  // CWS-1780: the host's own answer; a past membership is refunded from Orders, never here.
  const cancelVisible = cancelAvailable && source.is_live;
  // CWS-1536: a paused membership still renews on a card, so a pause does not hide Payment method.
  const paymentMethodVisible = canManage && source.is_live && paymentMethodAvailable;
  // CWS-1581: the host's own answer; a past membership never starts a family.
  const startFamilyVisible = startFamilyAvailable && source.is_live;
  // CWS-1734: the menu goes with its last item; it is never shown with nothing offered inside.
  const visible =
    source.is_live &&
    (updateVisible ||
      pauseVisible ||
      showCancelScheduled ||
      cancelVisible ||
      paymentMethodVisible ||
      startFamilyVisible);

  return {
    state,
    stat: pauseStat(pause, state),
    banner: bannerFor(state),
    showActivate: canManage && source.is_live && pause?.can_resume === true,
    manage: {
      visible,
      updateVisible,
      pauseVisible,
      showCancelScheduled,
      cancelVisible,
      paymentMethodVisible,
      startFamilyVisible,
    },
  };
}

function pauseStat(pause: SubscriptionPause | null, state: PauseState): PauseStat | null {
  if (state === 'paused') {
    const date = toDate(pause?.resume_date);
    return date ? { key: 'resumes_on', date } : null;
  }
  if (state === 'scheduled') {
    const date = toDate(pause?.start);
    return date ? { key: 'pause_starts', date } : null;
  }
  return null;
}

function bannerFor(state: PauseState): 'paused' | 'scheduled' | null {
  if (state === 'paused') {
    return 'paused';
  }
  return state === 'scheduled' ? 'scheduled' : null;
}
